/**
 * Browser translators (Chrome Translate, Edge Translator) replace text nodes
 * that React rendered. The reconciler then calls `removeChild` / `insertBefore`
 * on a parent that no longer owns the node and throws `NotFoundError`, which
 * takes the page down (facebook/react#11538).
 *
 * removeChild no-ops when the child was moved, so we do not pull it back out
 * of the translator's tree. insertBefore appends a new node when its
 * reference was moved, so the update still lands. A node already inside that
 * parent stays put, including one nested in the translator's wrapper, and a
 * node that contains the parent is left alone so appendChild cannot throw.
 * The first mismatch is logged once.
 *
 * Client-only. Idempotent across repeated calls and fast refresh.
 */

const INSTALLED_FLAG = '__curipodBrowserTranslateDomGuard';

type GuardedFn = {
  [INSTALLED_FLAG]?: boolean;
};

let didLogSkippedMutation = false;

const logSkippedMutation = (method: 'removeChild' | 'insertBefore') => {
  if (didLogSkippedMutation) return;
  didLogSkippedMutation = true;
  const detail =
    method === 'insertBefore'
      ? 'the reference is not a child of this parent'
      : 'the node is not a child of this parent';
  // eslint-disable-next-line no-console
  console.error(
    `[browser-translate-dom-guard] skipped Node.${method}: ${detail} (browser translation or an extension moved it)`,
  );
};

export function installBrowserTranslateDomGuard(): void {
  if (typeof window === 'undefined' || typeof Node !== 'function') return;

  const current = Node.prototype.removeChild as typeof Node.prototype.removeChild &
    GuardedFn;
  if (current[INSTALLED_FLAG]) return;

  const originalRemoveChild = Node.prototype.removeChild;
  const originalInsertBefore = Node.prototype.insertBefore;

  const removeChild = function<T extends Node>(this: Node, child: T): T {
    if (child && child.parentNode !== this) {
      logSkippedMutation('removeChild');
      return child;
    }
    return originalRemoveChild.call(this, child) as T;
  };

  const insertBefore = function<T extends Node>(
    this: Node,
    newNode: T,
    referenceNode: Node | null,
  ): T {
    if (referenceNode && referenceNode.parentNode !== this) {
      logSkippedMutation('insertBefore');
      // Already in this parent: a direct child, or nested in the translator's
      // wrapper. Moving it would reorder it or pull it out of that wrapper.
      // An ancestor cannot be appended here; that throws HierarchyRequestError.
      if (this.contains(newNode) || newNode.contains(this)) {
        return newNode;
      }
      try {
        return this.appendChild(newNode) as T;
      } catch (_err) {
        return newNode;
      }
    }
    return originalInsertBefore.call(this, newNode, referenceNode) as T;
  };

  (removeChild as GuardedFn)[INSTALLED_FLAG] = true;
  (insertBefore as GuardedFn)[INSTALLED_FLAG] = true;

  Node.prototype.removeChild = removeChild;
  Node.prototype.insertBefore = insertBefore;
}

/**
 * True when an error is the translator/extension DOM mismatch React throws
 * from `removeChild` / `insertBefore`. Used to tag the Faro context so the
 * critical alert can exclude it the way curi.live does.
 */
export function isLikelyBrowserExtensionCrash(error: unknown): boolean {
  if (!error || typeof error !== 'object') return false;
  const { name, message } = error as { name?: unknown; message?: unknown };
  if (name !== 'NotFoundError' || typeof message !== 'string') return false;
  const isRemoveOrInsert =
    message.includes('removeChild') || message.includes('insertBefore');
  return isRemoveOrInsert && message.includes('not a child of this node');
}
